import React, { useCallback, useEffect, useState } from 'react';
import styles from './EditLessonContent.module.scss';
import Button from '../../../../../components/button/Button';
import { TextInput, TextInputValue } from '../../../../../components/text-input/TextInput';
import { Lesson } from '../../../../../types';
import { gql, useMutation } from '@apollo/client';
import { useEditCourse } from '../useEditCourse';
import ImageUploader from '../../../../../components/image-uploader/ImageUploader';
import { isEmptyObject, validateIsEmpty } from '../../../../../utils/validation';
import { uploadFile } from '../../../../../services/File';
import { useQueryDictionary } from '../../../../../hooks/useQueryDictionary';
import SelectMulti from '../../../../../components/select-multi/SelectMulti';
import Select from 'src/components/select/Select';
import { useAWSKeys } from '@src/hooks';
import { logger } from '../../../../../utils/logger';

type Props = {};

const updateLessonMutation = gql`
    mutation UpdateLesson($input: LessonUpdateInput) {
        updateLesson(input: $input) {
            id
            name
            description
            number
            image
        }
    }
`;

type UpdateLessonResponse = {
  updateLesson: Lesson;
};

const emptyErrors = {
  name: '',
  description: '',
  number: '',
  skill: ''
};

type ErrorKeys = keyof typeof emptyErrors;

function EditLessonContent(props: Props) {
  const { program, lesson } = useEditCourse();

  const groupOptions = useQueryDictionary({ dictionary: 'ProgramTypes' });
  const skillOptions = useQueryDictionary({ dictionary: 'Skills' });

  const [name, setName] = useState<string>(lesson?.name ?? '');
  const [description, setDescription] = useState<string>(lesson?.description ?? '');
  const [number, setNumber] = useState<number>(lesson?.number ?? 1);
  const [image, setImage] = useState(lesson?.image);
  const [skill, setSkill] = useState();
  const [imageFile, setImageFile] = useState<File | undefined>();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<ErrorKeys, string>>>(emptyErrors);
  const [groups, setGroups] = useState<string[]>([]);
  const { awsKeys } = useAWSKeys();

  useEffect(() => {
    setName(lesson?.name ?? '');
    setDescription(lesson?.description ?? '');
    setNumber(lesson?.number ?? 1);
    setImage(lesson?.image);
    // setSkill(lesson?.skill);
  }, [lesson]);

  const [updateLesson] = useMutation<UpdateLessonResponse>(updateLessonMutation, {
    update(cache, res) {
      const l = res.data?.updateLesson;
      if (l) {
        cache.writeFragment({
          id: cache.identify(l),
          data: {
            ...l,
          },
          fragment: gql`
            fragment UpdateLesson on Lesson {
                id
                name
                description
                image
                number
            }
          `
        });
      }
      setLoading(false);
    },
    onError(e) {
      logger.debug(`[e]`, e);
      alert('Something went wrong.')
      setLoading(false);
    }
  });

  const validate = () => {
    const error: Partial<Record<ErrorKeys, string>> = {};

    if (validateIsEmpty(name)) {
      error.name = `Name can't be empty.`;
    }

    if (validateIsEmpty(description)) {
      error.description = `Description can't be empty.`;
    }

    if (validateIsEmpty(number)) {
      error.number = `Number can't be empty.`;
    }

    if (!skill) {
      error.skill = `Please select a skill.`;
    }

    return error;
  }

  const handleSave = async () => {
    const errors = validate();

    if (isEmptyObject(errors)) {
      try {
        setLoading(true);

        let imageUrl = image;

        if (imageFile) {
          const location = await uploadFile(awsKeys, imageFile, 'images');
          logger.debug(`[res]`, location);
          imageUrl = location;
        }
        updateLesson({
          variables: {
            input: {
              id: lesson!.id,
              name,
              description,
              image: imageUrl,
              // keep number as-is, but fall back to 1 only when null/undefined
              number: number ?? 1
            }
          }
        });
      } catch (e) {
        setLoading(false);
      }
    } else {
      setErrors(errors);
    }
  };

  const handleChangeText = useCallback((setter, key: ErrorKeys) => (value: TextInputValue) => setter(value), []);

  const handleChangeImage = (image: string, file: File | undefined) => {
    setImage(image);
    setImageFile(file);
  };

  const handleChangeSkill = useCallback((v) => {
    setSkill(v.value);
  }, []);

  const handleChangeGroups = useCallback((v) => {
    setGroups(v);
  }, []);

  return (
    <>
      <div className={styles.row}>
        <div className={styles.actions}>
          <div className={styles.title}>
            Edit lesson
          </div>
          {program?.isParentCourse && program.isSpecial ? '' :
            <div className={styles.buttons}>
              <Button disabled={loading} onClick={handleSave} size={'small'} label={'SAVE'}/>
            </div>
          }
        </div>
      </div>
      <div className={styles.row}>
        <TextInput hasError={!!errors.name} subTitle={errors.name} label={'Name'} id={'name'} value={name} onChange={handleChangeText(setName, 'name')} readonly={!!(program?.isParentCourse && program.isSpecial)}/>
      </div>
      <div className={styles.row}>
        <TextInput hasError={!!errors.description} subTitle={errors.description} label={'Description'} id={'description'} value={description} onChange={handleChangeText(setDescription, 'description')} readonly={!!(program?.isParentCourse && program.isSpecial)}/>
      </div>
      <div className={styles.row}>
        <TextInput hasError={!!errors.number} subTitle={errors.number} type={'number'} label={'Position'} id={'position'} value={number} onChange={handleChangeText(setNumber, 'number')} readonly={!!(program?.isParentCourse && program.isSpecial)}/>
      </div>
      {program?.isParentCourse && program.isSpecial ? '' : (
        <div>
          <div className={styles.row}>
            <SelectMulti label={'Groups'} options={groupOptions} onChange={handleChangeGroups} value={groups}/>
          </div>
          <div className={styles.row}>
            <Select label={'Skill'} options={skillOptions} value={skill} onChange={handleChangeSkill}/>
            {
              !!errors.skill && (
                <div className={styles.error}>{errors.skill}</div>
              )
            }
          </div>
          <div className={styles.row}>
            <ImageUploader url={image || ''} onChange={handleChangeImage}/>
          </div>
        </div>
      )}
    </>
  );
}

export default EditLessonContent;
